/**
 * signedUrlCache.ts
 *
 * Cache pour les signed URLs Supabase Storage
 * PR-06 : Éviter de re-signer en boucle
 */

interface CachedSignedUrl {
  url: string;
  expiresAt: number;
}

/**
 * Cache mémoire pour les signed URLs
 * Structure : Map<path, { url: string, expiresAt: number }>
 */
const signedUrlCache = new Map<string, CachedSignedUrl>();

/**
 * TTL par défaut (5 minutes avant expiration réelle)
 * On expire le cache 5 minutes avant l'expiration réelle de la signed URL
 */
const CACHE_BUFFER_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Récupère une signed URL depuis le cache ou la crée
 *
 * @param createSignedUrlFn - Fonction pour créer la signed URL si absente du cache
 * @param path - Chemin du fichier
 * @param ttl - TTL de la signed URL en secondes
 * @returns Promise<string> - La signed URL
 */
export async function getCachedSignedUrl(
  createSignedUrlFn: () => Promise<{ data: { signedUrl: string } | null; error: unknown }>,
  path: string,
  ttl: number = 3600
): Promise<string> {
  // Vérifier le cache
  const cached = signedUrlCache.get(path);
  if (cached) {
    const now = Date.now();
    // Expirer 5 minutes avant l'expiration réelle
    const expiresAtWithBuffer = cached.expiresAt - CACHE_BUFFER_MS;

    if (now < expiresAtWithBuffer) {
      return cached.url;
    }
    // Cache expiré, on le supprime
    signedUrlCache.delete(path);
  }

  // Créer la signed URL
  const { data, error } = await createSignedUrlFn();

  if (error || !data?.signedUrl) {
    throw new Error(`Erreur lors de la création de la signed URL: ${error}`);
  }

  // Mettre en cache
  const expiresAt = Date.now() + ttl * 1000;
  signedUrlCache.set(path, {
    url: data.signedUrl,
    expiresAt,
  });

  return data.signedUrl;
}

/**
 * Invalide le cache pour un chemin (ou tous)
 */
export function invalidateSignedUrlCache(path?: string): void {
  if (path) {
    signedUrlCache.delete(path);
  } else {
    signedUrlCache.clear();
  }
}

/**
 * Nettoie le cache des URLs expirées
 */
export function cleanupExpiredCache(): void {
  const now = Date.now();
  for (const [path, cached] of signedUrlCache.entries()) {
    if (now >= cached.expiresAt) {
      signedUrlCache.delete(path);
    }
  }
}

// NEUTRALISÉ: setInterval supprimé - géré par hook React useSignedUrlCacheCleanup
// Le hook useSignedUrlCacheCleanup remplace cette fonctionnalité
// if (typeof window !== 'undefined') {
//   setInterval(cleanupExpiredCache, 10 * 60 * 1000);
// }
